import React, { useContext, useEffect, useState } from "react";
import modalStyles from "./styles/AssetManagement.module.css";
import styles from "../Asset Evaluation/styles/AssetEvaluation.module.css";
import Dropdown from "../Asset Evaluation/Dropdown";
import { LanguageContext } from "../../../Context/changeLanguageProvider";
import {
  CURRENCY_OPTIONS,
  DEBT_TYPE_GROUPS,
  defaultCurrencyFor,
} from "../Asset Evaluation/AssetEvaluation";
import {
  displayAmount,
  missingDebtFields,
  toInputDate,
} from "../../../Utils/assetEvaluationUtils";
import ReportDateField from "../Asset Dashboard/ReportDateField";

/*
  Editing the Debt Value cell inline works only once the asset already has a
  debt: the amount is a reading, and a reading needs the description it is a
  reading OF — the type, the payoff date and the interest rate. None of those
  fit in a table cell.

  So an asset with no debt yet opens this instead, exactly as ownership opens
  its own modal rather than being editable in place.

  The fields are the debt block from the Add / Edit Asset modal, reproduced
  here: same two-row grid, same widths, same controls, and the same stylesheet
  (`AssetEvaluation.module.css`) rather than a second set of modal field styles
  that would drift from it. Only the shell — backdrop, title, buttons — comes
  from this page's own stylesheet, so it sits alongside the ownership modal.
*/

const FALLBACK = {
  assetManagementText17: "Save",
  assetManagementText18: "Cancel",
  assetManagementText24: "Add debt",
};

const EVAL_FALLBACK = {
  assetEvaluationText8: "Currency",
  assetEvaluationText16: "Select",
  assetEvaluationText27: "Type of Debt",
  assetEvaluationText28: "Debt Amount",
  assetEvaluationText29: "Annual Interest (%)",
  assetEvaluationText30: "Payoff Date",
  assetEvaluationText52: "Error: Please fill in all the fields shown",
  assetEvaluationText57: "Asset-Linked Debts",
  assetEvaluationText58: "General / Standalone Liabilities",
};

// Digits plus at most one decimal point; never negative.
function parsePercent(raw) {
  const cleaned = String(raw).replace(/[^\d.]/g, "");
  const parts = cleaned.split(".");
  return parts.length > 1 ? `${parts[0]}.${parts.slice(1).join("")}` : parts[0];
}

// A debt is a liability: the minus sign is not the user's to remove.
function parseDebtAmount(raw) {
  const digits = String(raw).replace(/\D/g, "");
  return digits === "" ? "" : `-${digits}`;
}

// The panel renders the unit inline, right after the number.
const displayPercent = (raw) => (raw === "" ? "" : `${raw}%`);

function DebtModal({ asset, onClose, onSave }) {
  const websiteCurrentLang = useContext(LanguageContext).language;

  const [content, setContent] = useState(
    websiteCurrentLang?.content?.User?.AssetManagement?.AssetManagement ||
      FALLBACK
  );
  const [evaluationContent, setEvaluationContent] = useState(
    websiteCurrentLang?.content?.User?.EditAsset?.AssetEvaluation
  );
  useEffect(() => {
    setContent(
      websiteCurrentLang?.content?.User?.AssetManagement?.AssetManagement ||
        FALLBACK
    );
    setEvaluationContent(
      websiteCurrentLang?.content?.User?.EditAsset?.AssetEvaluation
    );
  }, [websiteCurrentLang]);

  const t = (key) => (content && content[key]) || FALLBACK[key];
  const tEval = (key) =>
    (evaluationContent && evaluationContent[key]) || EVAL_FALLBACK[key];

  /*
    Prefilled from the asset when it already has a debt described, so this can
    also correct one. The AMOUNT always starts empty: it is a new reading, not
    an edit of the last one.
  */
  const [debtType, setDebtType] = useState(asset?.debt_type || "");
  const [payoff, setPayoff] = useState(toInputDate(asset?.debt_payoff_date));
  const [interest, setInterest] = useState(asset?.debt_annual_interest ?? "");
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState(
    asset?.debt_currency || defaultCurrencyFor(websiteCurrentLang?.label)
  );
  const [showError, setShowError] = useState(false);

  /*
    What a debt has to say for itself, decided by the same rule the Add / Edit
    Asset panel validates against -- see missingDebtFields. Sharing it is the
    point: this modal writes the debt and that panel checks it afterwards, so
    two copies of the rule could disagree and lock an asset out of its own
    editor.

    `amountRequired` because this modal exists precisely to record an amount;
    saving without one would leave the user where they started. A bare "-" is
    what the field holds after the minus is pinned and nothing typed after it.
  */
  const missing = missingDebtFields(
    {
      debt_type: debtType,
      debt_amount: amount === "-" ? "" : amount,
      debt_currency: currency,
      debt_annual_interest: interest,
      debt_payoff_date: payoff,
    },
    { amountRequired: true }
  );
  const valid = missing.size === 0;
  const invalid = (key) =>
    showError && missing.has(key) ? styles.field_invalid : "";

  const commit = () => {
    if (!valid) {
      setShowError(true);
      return;
    }
    onSave({
      debt_type: debtType,
      debt_payoff_date: payoff,
      debt_annual_interest: interest,
      debt_amount: amount,
      debt_currency: currency,
    });
  };

  const debtTypeGroups = DEBT_TYPE_GROUPS.map((group) => ({
    label: tEval(group.labelKey),
    options: group.options.map((option) => ({
      value: option.value,
      label: tEval(option.labelKey),
    })),
  }));

  return (
    <div className={modalStyles.modal_backdrop} onClick={onClose}>
      <div
        className={`${modalStyles.modal} ${modalStyles.modal_wide}`}
        role="dialog"
        aria-label={t("assetManagementText24")}
        onClick={(e) => e.stopPropagation()}
      >
        <h3
          className={`${modalStyles.modal_title} ${modalStyles.modal_title_center}`}
        >
          {t("assetManagementText24")}
        </h3>

        {/*
          `.section` carries the box-sizing reset the evaluation stylesheet
          relies on, and `.panel` its tinted background — the same surface the
          fields sit on in the Add / Edit Asset modal.
        */}
        <div className={styles.section}>
          <div className={styles.panel}>
            {/* Type of debt on its own row: its labels are the longest here. */}
            <div className={modalStyles.debt_modal_row_full}>
              <div className={styles.cell}>
                <div className={`${styles.label} ${styles.label_required}`}>
                  {tEval("assetEvaluationText27")}
                </div>
                <Dropdown
                  value={debtType}
                  onChange={(next) => {
                    setDebtType(next);
                    setShowError(false);
                  }}
                  groups={debtTypeGroups}
                  placeholder={tEval("assetEvaluationText16")}
                  className={invalid("debt_type")}
                  ariaLabel={tEval("assetEvaluationText27")}
                />
              </div>
            </div>

            <div className={modalStyles.debt_modal_row_half}>
              <div className={styles.cell}>
                <div className={`${styles.label} ${styles.label_required}`}>
                  {tEval("assetEvaluationText28")}
                </div>
                <input
                  type="text"
                  inputMode="numeric"
                  className={`${styles.field} ${invalid("debt_amount")}`}
                  placeholder="-0"
                  value={displayAmount(amount)}
                  /*
                    The minus is pinned, so the caret is pushed back to the end
                    after every keystroke — otherwise it would wedge between the
                    sign and the digits.
                  */
                  onChange={(e) => {
                    const input = e.currentTarget;
                    setAmount(parseDebtAmount(input.value));
                    setShowError(false);
                    window.requestAnimationFrame(() => {
                      if (document.activeElement === input) {
                        const end = input.value.length;
                        input.setSelectionRange(end, end);
                      }
                    });
                  }}
                />
              </div>

              <div className={styles.cell}>
                <div className={styles.label}>{tEval("assetEvaluationText8")}</div>
                <select
                  className={styles.select}
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                >
                  {CURRENCY_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className={modalStyles.debt_modal_row_half}>
              <div className={styles.cell}>
                <div className={styles.label}>{tEval("assetEvaluationText29")}</div>
                <input
                  type="text"
                  inputMode="decimal"
                  className={`${styles.field} ${styles.field_percent}`}
                  placeholder="0%"
                  value={displayPercent(interest)}
                  onChange={(e) => {
                    setInterest(parsePercent(e.target.value));
                    setShowError(false);
                  }}
                />
              </div>

              <div className={styles.cell}>
                <div className={styles.label}>{tEval("assetEvaluationText30")}</div>
                {/*
                  Ours, not the browser's: a native date input is drawn in the
                  BROWSER's locale and reads DD/MM even with the site in
                  English. Same field class, same box; only the format is ours.
                */}
                <ReportDateField
                  className={`${styles.field} ${styles.field_date}`}
                  value={payoff}
                  onChange={(next) => {
                    setPayoff(next);
                    setShowError(false);
                  }}
                  french={websiteCurrentLang?.label === "fr"}
                  ariaLabel={tEval("assetEvaluationText30")}
                />
              </div>
            </div>

            {showError && !valid && (
              <div className={styles.panel_error}>
                {tEval("assetEvaluationText52")}
              </div>
            )}
          </div>
        </div>

        <div className={modalStyles.modal_actions}>
          <button
            type="button"
            className={modalStyles.modal_cancel}
            onClick={onClose}
          >
            {t("assetManagementText18")}
          </button>
          <button
            type="button"
            className={modalStyles.modal_save}
            onClick={commit}
          >
            {t("assetManagementText17")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default DebtModal;
